import {
  type ConfigExtension,
  type DefaultClassGroupIds,
  type DefaultThemeGroupIds,
  extendTailwindMerge,
} from "tailwind-merge";

type AlouetteClassGroupIds =
  | DefaultClassGroupIds
  | "focus-ring"
  | "lowered"
  | "surface-flat"
  | "surface-popover"
  | "surface-size"
  | "surface"
  | "text-trim";

const paddingAndRadiusGroups = [
  "p",
  "px",
  "py",
  "ps",
  "pe",
  "pbs",
  "pbe",
  "pt",
  "pr",
  "pb",
  "pl",
  "rounded",
  "rounded-s",
  "rounded-e",
  "rounded-t",
  "rounded-r",
  "rounded-b",
  "rounded-l",
  "rounded-ss",
  "rounded-se",
  "rounded-ee",
  "rounded-es",
  "rounded-tl",
  "rounded-tr",
  "rounded-br",
  "rounded-bl",
] as const satisfies readonly DefaultClassGroupIds[];

/**
 * tailwind-merge only knows Tailwind's default scale, so alouette's named
 * spacing (`p-m` / `p-xl`) and custom utilities would otherwise survive side by
 * side and resolve by stylesheet order instead of by the caller's `className`.
 */
export const twMergeConfig: ConfigExtension<
  AlouetteClassGroupIds,
  DefaultThemeGroupIds
> = {
  extend: {
    theme: {
      spacing: [
        "xxs",
        "xs",
        "sm",
        "m", // legacy
        "md",
        "l", // legacy
        "lg",
        "xl",
        "xxl",
        "3xl",
        "4xl",
        "control-sm",
        "control-md",
        "control-lg",
        "control-chip",
        "control-chip-icon",
        "control-chip-stacked",
        "control-inset",
      ],
      shadow: ["s", "m", "l", "lowered", "bar"],
    },
    classGroups: {
      "font-family": [
        "font-body",
        "font-body-bold",
        "font-body-extrabold",
        "font-heading",
        "font-heading-bold",
        "font-heading-extrabold",
        "font-mono",
        "font-mono-bold",
        "font-mono-extrabold",
      ],
      "focus-ring": ["focus-ring", "focus-ring-inset"],
      lowered: ["lowered"],
      surface: ["surface"],
      "surface-flat": ["surface-flat"],
      "surface-size": [{ surface: ["xxs", "xs", "sm", "md", "lg"] }],
      "surface-popover": ["surface-popover"],
      "text-trim": ["text-trim-cap"],
    },
    // A group lists what it replaces when written after it. A single class
    // written after one of these utilities never lists them back: it only
    // overrides its own property (the utilities sort ahead of it), so
    // `surface bg-highlight` keeps the padding and `surface-popover surface-lg`
    // keeps the ground.
    conflictingClassGroups: {
      "focus-ring": ["outline-w", "outline-offset", "outline-color"],
      lowered: ["bg-color", "shadow"],
      surface: [
        ...paddingAndRadiusGroups,
        "bg-color",
        "shadow",
        "surface-flat",
        "surface-popover",
        "surface-size",
      ],
      "surface-flat": [
        ...paddingAndRadiusGroups,
        "bg-color",
        "border-color",
        "border-w",
        "surface",
        "surface-popover",
        "surface-size",
      ],
      "surface-size": [...paddingAndRadiusGroups],
      "surface-popover": [
        ...paddingAndRadiusGroups,
        "bg-color",
        "shadow",
        "surface",
        "surface-flat",
        "surface-size",
      ],
    },
  },
};

export const twMerge =
  extendTailwindMerge<AlouetteClassGroupIds>(twMergeConfig);
